import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Clock, XCircle, Cpu, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DevisPaiementSuccesPage = () => {
  const { devisId } = useParams();
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/api/devis/${devisId}/payment-status`);
        setStatus(res.data);
        if (res.data.payment_status === 'pending' && attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 3000);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [devisId, attempts]);

  if (loading && !status) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const isPaid = status?.payment_status === 'paid';
  const isPartial = status?.payment_status === 'partial';
  const isPending = status?.payment_status === 'pending';

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="devis-paiement-succes-page">
      <div className="max-w-lg w-full space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center mb-4">
            <Cpu className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">R.E.Mobiles</h1>
        </div>

        <div className="card-dark p-6 text-center space-y-4">
          {isPaid ? (
            <>
              <CheckCircle className="w-20 h-20 text-green-400 mx-auto" />
              <h2 className="text-2xl font-bold text-white">{t('paySuccess.confirmed')}</h2>
              <p className="text-gray-400">{t('paySuccess.paymentFor')} <strong className="text-violet-400">{status?.devis_ref}</strong> {t('paySuccess.registered')}</p>
            </>
          ) : isPartial ? (
            <>
              <CreditCard className="w-20 h-20 text-blue-400 mx-auto" />
              <h2 className="text-2xl font-bold text-white">{t('paySuccess.depositRegistered')}</h2>
              <p className="text-gray-400">{t('paySuccess.depositFor')} <strong className="text-violet-400">{status?.devis_ref}</strong> {t('paySuccess.depositDone')}</p>
            </>
          ) : isPending && attempts < 10 ? (
            <>
              <Clock className="w-20 h-20 text-yellow-400 mx-auto animate-pulse" />
              <h2 className="text-2xl font-bold text-white">{t('paySuccess.checking')}</h2>
              <p className="text-gray-400">{t('paySuccess.pleaseWait')}</p>
              <div className="loading-spinner mx-auto"></div>
            </>
          ) : (
            <>
              <Clock className="w-20 h-20 text-yellow-400 mx-auto" />
              <h2 className="text-2xl font-bold text-white">{t('paySuccess.waitingConfirm')}</h2>
              <p className="text-gray-400">{t('paySuccess.processing')}</p>
            </>
          )}

          {status && (
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between p-2 rounded bg-white/5">
                <span className="text-gray-400">{t('paySuccess.totalInvoice')}</span>
                <span className="text-white font-semibold">{parseFloat(status.total || 0).toFixed(2)} €</span>
              </div>
              <div className="flex justify-between p-2 rounded bg-white/5">
                <span className="text-gray-400">{t('paySuccess.amountPaid')}</span>
                <span className="text-green-400 font-semibold">{parseFloat(status.amount_paid || 0).toFixed(2)} €</span>
              </div>
              {parseFloat(status.remaining || 0) > 0 && (
                <div className="flex justify-between p-2 rounded bg-white/5">
                  <span className="text-gray-400">{t('paySuccess.remaining')}</span>
                  <span className="text-amber-400 font-semibold">{parseFloat(status.remaining || 0).toFixed(2)} €</span>
                </div>
              )}
            </div>
          )}

          {isPartial && parseFloat(status?.remaining || 0) > 0 && (
            <a href={`/devis/paiement/${devisId}?mode=complet`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors mt-4"
              data-testid="pay-remaining-btn">
              <CreditCard className="w-5 h-5" /> {t('paySuccess.payRemaining')} ({parseFloat(status.remaining).toFixed(2)} €)
            </a>
          )}
        </div>

        <div className="text-center">
          <a href="/login" className="text-violet-400 hover:text-violet-300 text-sm">
            {t('paySuccess.loginClient')}
          </a>
        </div>

        <p className="text-center text-xs text-gray-600">
          R.E.Mobiles - 102 Avenue General de Gaulle, Porte 2, 97300 Cayenne
        </p>
      </div>
    </div>
  );
};

export default DevisPaiementSuccesPage;
